import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, X, Loader2, CheckCircle2, Phone } from "lucide-react";
import { API } from "../lib/api";
import { useContentText } from "../lib/content";
import { toast } from "sonner";

/**
 * Specialist Enquiry modal - opens when the searched name is on NBR's protected
 * register (i.e. report.nbr_available === false). Captures a lead with
 * source='specialist_enquiry' so Sophie can pick up the conversation.
 */
export default function SpecialistEnquiry({
  businessName = "",
  matchedName = null,
  buttonClass = "btn-primary",
  buttonLabel = "Speak to a specialist",
  testid = "specialist-enquiry",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const phone = useContentText("footer.contact.phone", "0800 069 9090");
  const phoneDigits = phone.replace(/\D/g, "");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/enquiry/specialist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          business_name: businessName || "Unknown",
          notes: form.notes.trim() || (matchedName ? `Search matched protected name: ${matchedName}` : null),
        }),
      });
      if (!res.ok) {
        let msg = "Could not send your enquiry. Please try again.";
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch { /* fall back to default msg */ }
        throw new Error(msg);
      }
      setDone(true);
      // GA4 `specialist_enquiry_sent` - fires only after the backend accepts
      // the lead. `has_matched_name` distinguishes true NBR-register conflicts
      // from open-ended "not sure" enquiries triggered by Critical risk scores.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "specialist_enquiry_sent", {
          business_name: businessName || "Unknown",
          has_matched_name: Boolean(matchedName),
        });
      }
      toast.success("Enquiry received. A specialist will be in touch within one working day.");
    } catch (err) {
      toast.error(err.message || "Could not send your enquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    // tiny delay so the modal slides out before resetting
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", phone: "", notes: "" });
    }, 250);
  };

  // Lock body scroll while the modal is open so the page underneath doesn't
  // shift behind the overlay. Also closes the modal on Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !busy) close(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy]);

  // Rendered via Portal so the overlay attaches to <body>, escaping any
  // parent containing-block (`transform`, `filter`, `perspective`) that
  // would otherwise cause `position: fixed` to be relative to that ancestor.
  const modal = open ? (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 sm:p-4 animate-in fade-in overflow-y-auto"
      onClick={close}
      data-testid={`${testid}-modal`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 sm:p-7 md:p-8 relative bnp-hero-shadow my-auto max-h-[calc(100vh-1.5rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#9ac7f7]/20 text-[#447694]"
          aria-label="Close"
          data-testid={`${testid}-close`}
        >
          <X size={18} />
        </button>

        {done ? (
          <div data-testid={`${testid}-success`}>
            <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 size={22} className="text-emerald-600" />
            </div>
            <h3 className="font-display text-2xl text-[#04273d] mt-4 font-bold tracking-tight">Thanks {form.name.split(" ")[0] || "for getting in touch"}.</h3>
            <p className="text-sm text-[#447694] mt-3 leading-relaxed">
              A UK specialist will review your enquiry about <span className="font-semibold text-[#04273d]">{businessName}</span> and get back to you within one working day.
            </p>
            <button type="button" onClick={close} className="btn-secondary w-full justify-center mt-6">Close</button>
          </div>
        ) : (
          <>
            <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <MessageSquare size={22} className="text-amber-600" />
            </div>
            <h3 className="font-display text-2xl text-[#04273d] mt-4 font-bold tracking-tight">Speak to a specialist</h3>
            <p className="text-sm text-[#447694] mt-2 leading-relaxed">
              Your search for <span className="font-semibold text-[#04273d]">{businessName || "this name"}</span> overlaps with an existing protected business{matchedName ? <> (<span className="italic">{matchedName}</span>)</> : ""}. A specialist will review and recommend the right next step. Free, no obligation.
            </p>

            {/* "Prefer to call?" - fastest route to a human, Mon-Fri 9-5 */}
            <a
              href={`tel:${phoneDigits}`}
              className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-[#0d9b80] bg-emerald-50 px-4 py-3 hover:bg-emerald-100 transition-colors"
              data-testid={`${testid}-call-us-button`}
            >
              <div className="h-10 w-10 rounded-xl bg-[#0d9b80] flex items-center justify-center shrink-0">
                <Phone size={18} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-[#04273d] text-sm">Prefer to call us? Call us now</p>
                <p className="font-mono-brand text-[11px] text-[#447694] mt-0.5 truncate">
                  <span className="text-[#04273d] font-semibold">{phone}</span> &middot; Mon-Fri, 9am-5pm UK
                </p>
              </div>
            </a>

            <div className="my-4 flex items-center gap-3 text-[10px] font-mono-brand uppercase tracking-widest text-[#447694]">
              <div className="h-px flex-1 bg-[#9ac7f7]/40" />
              Or send a message
              <div className="h-px flex-1 bg-[#9ac7f7]/40" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid={`${testid}-form`}>
              <div>
                <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Sophie Bell"
                  className="bnp-input"
                  data-testid={`${testid}-name`}
                />
              </div>
              <div>
                <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@yourbusiness.co.uk"
                  className="bnp-input"
                  data-testid={`${testid}-email`}
                />
              </div>
              <div>
                <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Phone (optional)</label>
                <input
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="07700 900123"
                  className="bnp-input"
                  data-testid={`${testid}-phone`}
                />
              </div>
              <div>
                <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Anything we should know? (optional)</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="When you started trading, area you operate in, etc."
                  className="bnp-input"
                  data-testid={`${testid}-notes`}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center mt-2"
                data-testid={`${testid}-submit`}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                {busy ? "Sending…" : "Send my enquiry"}
              </button>
              <p className="text-[11px] text-[#447694] text-center leading-relaxed">
                By submitting, you agree to receive our <span className="font-semibold text-[#04273d]">monthly newsletter and occasional intellectual-property updates</span> from NBR Group. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClass}
        data-testid={`${testid}-trigger`}
      >
        <MessageSquare size={14} /> {buttonLabel}
      </button>
      {modal && typeof document !== "undefined" && createPortal(modal, document.body)}
    </>
  );
}
